import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startTime", "endTime", "duration", "preview"]

  connect() {
    this.updatePreview()
  }

  updatePreview() {
    const startTime = this.startTimeTarget.value.substring(0, 5) // Extract HH:MM from HH:MM:SS.SSS
    const endTime = this.endTimeTarget.value.substring(0, 5)     // Extract HH:MM from HH:MM:SS.SSS
    const duration = parseInt(this.durationTarget.value)

    if (!startTime || !endTime || !duration) {
      this.previewTarget.innerHTML = this.getEmptyPreviewHTML()
      return
    }

    const slots = this.generateTimeSlots(startTime, endTime, duration)
    this.renderPreview(slots)
  }

  generateTimeSlots(startTime, endTime, durationMinutes) {
    const slots = []
    const start = new Date(`2000-01-01 ${startTime}:00`)
    const end = new Date(`2000-01-01 ${endTime}:00`)

    if (start >= end) {
      return []
    }

    let currentTime = new Date(start)

    while (currentTime < end) {
      const slotEnd = new Date(currentTime.getTime() + (durationMinutes * 60 * 1000))
      
      if (slotEnd > end) {
        break
      }

      slots.push({
        start: this.formatTime(currentTime),
        end: this.formatTime(slotEnd),
        startDisplay: this.formatDisplayTime(currentTime),
        endDisplay: this.formatDisplayTime(slotEnd)
      })

      currentTime = new Date(slotEnd)
    }

    return slots
  }

  formatTime(date) {
    return date.toTimeString().slice(0, 5)
  }

  formatDisplayTime(date) {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    })
  }

  renderPreview(slots) {
    if (slots.length === 0) {
      this.previewTarget.innerHTML = this.getInvalidPreviewHTML()
      return
    }

    const slotsHTML = slots.map(slot => `
      <li class="px-4 py-4 sm:px-6 hover:bg-gray-50">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <div class="text-sm font-medium text-gray-900">
                ${slot.startDisplay} - ${slot.endDisplay}
              </div>
              <div class="text-sm text-gray-500">Available slot</div>
            </div>
          </div>
          <div class="flex items-center">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Available
            </span>
          </div>
        </div>
      </li>
    `).join('')

    this.previewTarget.innerHTML = `
      <div class="bg-white shadow overflow-hidden sm:rounded-md">
        <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Schedule Preview</h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-500">
            ${slots.length} time slots will be generated
          </p>
        </div>
        <ul class="divide-y divide-gray-200">
          ${slotsHTML}
        </ul>
      </div>
    `
  }

  getEmptyPreviewHTML() {
    return `
      <div class="bg-gray-50 rounded-lg p-8 text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No preview available</h3>
        <p class="mt-1 text-sm text-gray-500">Fill in all fields to see your schedule preview</p>
      </div>
    `
  }

  getInvalidPreviewHTML() {
    return `
      <div class="bg-red-50 rounded-lg p-8 text-center">
        <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">Invalid schedule</h3>
        <p class="mt-1 text-sm text-gray-500">End time must be after start time</p>
      </div>
    `
  }
}